import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import CodeBlock from '../../components/CodeBlock';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-data-uri-guide';

const FAQS: Faq[] = [
  { q: 'What is a Base64 data URI?', a: 'A data URI embeds a file directly inside HTML or CSS as text, in the form data:[mime-type];base64,[encoded-data], instead of linking to a separate file with a URL.' },
  { q: 'When should I use a data URI instead of a normal image file?', a: 'For small, frequently-reused assets (icons, tiny logos, inline fonts) where saving an extra HTTP request outweighs the ~33% size increase from Base64 encoding. For large or rarely-changed images, a normal linked file with caching is usually better.' },
  { q: 'Do data URIs work in CSS as well as HTML?', a: 'Yes — anywhere a URL is accepted, including background-image, list-style-image, @font-face src, and the src attribute of <img>, <video> or <script>.' },
  { q: 'Can browsers cache data URIs?', a: 'Not independently — a data URI is part of the HTML/CSS document itself, so it is cached (or not) along with that document, not as its own cacheable resource the way a linked file is.' },
];

export default function Base64DataUriGuidePage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Base64 Data URIs Explained: Embedding Images & Fonts in CSS/HTML | EasyBase64"
        description="Learn how Base64 data URIs work, how to embed images, fonts and SVGs directly in CSS or HTML, and when it's worth the size tradeoff."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Base64 Data URIs Explained: Embedding Images and Fonts in CSS/HTML',
            description: 'How Base64 data URIs work and when to use them to embed assets directly in CSS or HTML.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 Data URI Guide', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Base64 Data URIs Explained: Embedding Images and Fonts in CSS/HTML
          </h1>

          <p className={para}>
            A data URI lets you embed a file's actual bytes directly inside an HTML or CSS
            document, as text, instead of pointing to a separate file with a normal URL. Base64 is
            what makes that possible for binary files like images and fonts.
          </p>

          <h2 className={heading}>The anatomy of a data URI</h2>
          <p className={para}>
            A data URI has the form <code>data:[mime-type];base64,[encoded-data]</code>. For
            example, a tiny red-dot PNG becomes:
          </p>
          <CodeBlock
            language="HTML"
            code={`<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAAB..." />`}
          />
          <p className={para}>
            The browser decodes the Base64 portion back into raw image bytes and renders it exactly
            as if it had been loaded from a normal file — no network request required.
          </p>

          <h2 className={heading}>Using data URIs in CSS</h2>
          <p className={para}>
            The same technique works anywhere CSS accepts a URL:
          </p>
          <CodeBlock
            language="CSS"
            code={`.icon {
  background-image: url("data:image/svg+xml;base64,PASTE_BASE64_HERE");
}

@font-face {
  font-family: "MyIconFont";
  src: url("data:font/woff2;base64,PASTE_BASE64_HERE") format("woff2");
}`}
          />
          <p className={para}>
            This is especially common for small SVG icons — see the{' '}
            <Link to="/svg-to-base64" className="underline">SVG to Base64 tool</Link> to generate
            one, or the <Link to="/image-to-base64" className="underline">image to Base64 tool</Link>{' '}
            for raster images like PNG and JPEG.
          </p>

          <h2 className={heading}>The tradeoff: fewer requests, bigger payload</h2>
          <p className={para}>
            Inlining an asset removes a separate HTTP request, which can help for small,
            frequently-reused images (like a logo repeated across many pages) — especially over
            HTTP/1.1 where each request has overhead. But Base64 encoding adds about a{' '}
            <Link to="/base64-file-size-overhead" className="underline">33% size overhead</Link>,
            and an inlined asset can no longer be cached independently of the document it lives in.
            For large images, or assets shared across many pages that benefit from long-term browser
            caching, a normal linked file is usually the better choice.
          </p>

          <h2 className={heading}>Try it</h2>
          <p className={para}>
            Use the <Link to="/image-to-base64" className="underline">Image to Base64 tool</Link>{' '}
            to generate a data URI from any image file, or the general{' '}
            <Link to="/" className="underline">Base64 converter</Link> for any other text or file
            content.
          </p>
        </article>

        <FaqSection title="Base64 Data URIs — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
